import { useMemo, useState, useCallback } from "react";
import { Badge, Button, Flex, Table, Text, Box } from "@radix-ui/themes";
import { Filter, X, ChevronDown } from "lucide-react";

import { Search } from "@/components/shared/inputs/search";
import { useGetTopics } from "@/api/useGetTopics";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { Loader } from "@/components/ui/loader";

import { VersionItem } from "./version-item";
import styles from "./styles.module.scss";
import { VersionsNavBar } from "./versions-nav-bar";

type VersionStats = {
  instructionsCount: number;
  testedInteractionsCount: number;
  goodFeedbackCount: number;
  badFeedbackCount: number;
  topicsCount: number;
  topicsList: string[];
};

const ITEMS_PER_LOAD = 20;

function getTopicsListFromReleasePolicy(release: { releasePolicy?: unknown }): string[] {
  try {
    const policy =
      typeof release.releasePolicy === "string"
        ? JSON.parse(release.releasePolicy)
        : release.releasePolicy;
    if (!policy || !Array.isArray(policy)) return [];
    const disabledTopics = policy
      .filter(
        (instruction: { type?: string; content?: string }) =>
          instruction.type === "isEnabled" && instruction.content === "false"
      )
      .map((instruction: { topic?: string }) => instruction.topic);
    const topicNames = policy
      .filter(
        (instruction: { topic?: string }) => !disabledTopics.includes(instruction.topic)
      )
      .filter(
        (instruction: { type?: string; topic?: string; content?: string }) =>
          ((instruction.type === "manual" || instruction.type === "brief") &&
            instruction.topic &&
            instruction.topic.trim() !== "") ||
          (instruction.type === "isEnabled" && instruction.content === "true")
      )
      .map((instruction: { topic?: string }) => (instruction.topic ?? "").trim());
    return Array.from(new Set(topicNames));
  } catch (error) {
    console.error("[SimulatorVersions] getTopicsListFromReleasePolicy:", error);
    return [];
  }
}

export default function SimulatorVersions() {
  const [searchPhrase, setSearchPhrase] = useState("");
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [displayedItems, setDisplayedItems] = useState(ITEMS_PER_LOAD);
  const { data: topics }: { data: Topic[] | undefined } = useGetTopics();

  const { simulator, releasesWithVersions, currentRelease } = useSimulator();

  const sortedReleases = useMemo(() => {
    return [...(releasesWithVersions || [])].sort((a, b) => b.id - a.id);
  }, [releasesWithVersions]);

  const displayedReleases = useMemo(() => {
    return sortedReleases.slice(0, displayedItems);
  }, [sortedReleases, displayedItems]);

  const hasMoreItems = useMemo(() => {
    return displayedItems < sortedReleases.length;
  }, [displayedItems, sortedReleases.length]);

  // Reset to initial load when filters change
  const handleFilterChange = useCallback((topicIds: number[]) => {
    setSelectedTopicIds(topicIds);
    setDisplayedItems(ITEMS_PER_LOAD);
  }, []);

  const handleShowMore = useCallback(() => {
    setDisplayedItems((prev) => prev + ITEMS_PER_LOAD);
  }, []);

  const versionStatsMap: Record<number, VersionStats> = useMemo(() => {
    if (!releasesWithVersions || !topics) return {};

    const statsMap: Record<number, VersionStats> = {};

    releasesWithVersions.forEach((release) => {
      const instructionsCount =
        release.topicReleasePolicies?.reduce(
          (acc, policy) => acc + (policy.instructions?.length || 0),
          0
        ) || 0;

      const conversations = release.releaseConversations || [];
      const testedInteractionsCount = conversations.length;

      const goodFeedbackCount = conversations.filter(
        (conv) => conv.feedback === "POSITIVE"
      ).length;

      const badFeedbackCount = conversations.filter(
        (conv) => conv.feedback === "NEGATIVE"
      ).length;

      const beforeFilter = release.topicReleasePolicies || [];

      let topicsList =
        beforeFilter
          .filter(
            (policy) =>
              policy.isEnabled &&
              (policy.instructions?.length || 0) > 0
          )
          .map((policy) => {
            const topic = topics.find((t) => t.id === policy.topicId);
            return {
              topicId: policy.topicId,
              topicName: topic?.topic || `Topic ${policy.topicId}`,
            };
          })
          .sort((a, b) => a.topicId - b.topicId)
          .map((item) => item.topicName) || [];

      if (topicsList.length === 0 && release.releasePolicy) {
        topicsList = getTopicsListFromReleasePolicy(release);
      }

      const topicsCount = topicsList.length;

      statsMap[release.id] = {
        instructionsCount,
        testedInteractionsCount,
        goodFeedbackCount,
        badFeedbackCount,
        topicsCount,
        topicsList,
      };
    });

    return statsMap;
  }, [releasesWithVersions, topics]);

  if (!simulator || !releasesWithVersions || !topics) {
    return <Loader />;
  }

  return (
    <div className="w-full h-full flex flex-col">
      <VersionsNavBar />
      <Flex direction={"column"} gap={"8"} px={"8"} py={"8"}>
        <Flex direction={"row"} gap={"2"} justify={"between"}>
          <Flex direction={"column"} gap={"2"}>
            <h3 style={{ fontWeight: "500" }}>Versions</h3>
          </Flex>
        </Flex>
        <Flex direction={"column"} gap={"2"}>
          <Flex direction={"row"} gap={"2"} justify="between" align="center">
            <Flex direction={"row"} gap={"2"} align="center">
              <Flex gap={"2"} align={"center"}>
                <Text size={"3"} weight={"medium"}>
                  Versions
                </Text>
                <Badge color="gray">{releasesWithVersions?.length}</Badge>
              </Flex>
              <Search
                initialValue={searchPhrase}
                placeholder="Search versions "
                setSearchPhrase={setSearchPhrase}
                debounce={50}
              />
            </Flex>

            {topics && topics.length > 0 && (
              <DropdownMenu
                items={topics.map((topic) => ({
                  label: topic.topic,
                  onClick: () => {
                    handleFilterChange([topic.id]);
                  },
                }))}
              >
                <DropdownMenu.Trigger>
                  <Button variant="outline" color="gray">
                    <Flex gap="2" align="center">
                      <Filter size={14} />
                      Filter by Topic{" "}
                      {selectedTopicIds.length > 0 &&
                        `(${selectedTopicIds.length})`}
                    </Flex>
                  </Button>
                </DropdownMenu.Trigger>
              </DropdownMenu>
            )}
          </Flex>

          {selectedTopicIds.length > 0 && (
            <Flex gap="2" wrap="wrap" className={styles.activeFilters}>
              {selectedTopicIds.map((topicId, index) => (
                <Badge
                  key={`${topicId}-${index}`}
                  size="1"
                  variant="soft"
                  className={styles.filterChip}
                >
                  {topics.find((t) => t.id === topicId)?.topic}
                  <Box
                    as="span"
                    ml="1"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFilterChange(
                        selectedTopicIds.filter((t) => t !== topicId)
                      );
                    }}
                    className={styles.chipRemove}
                  >
                    <X size={12} />
                  </Box>
                </Badge>
              ))}
              <Box
                as="span"
                onClick={() => handleFilterChange([])}
                className={styles.clearFilters}
              >
                Clear all filters
              </Box>
            </Flex>
          )}

          <Table.Root>
            <Table.Header>
              <Table.Row className=" text-gray-500 font-normal">
                <Table.ColumnHeaderCell className=" font-normal w-1/4">
                  Version
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className=" font-normal w-1/4">
                  Released Topics
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className=" font-normal w-1/12">
                  Instructions
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className=" font-normal w-1/12">
                  Tested Sessions
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className=" font-normal w-1/12">
                  Good Feedback
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className=" font-normal w-1/12">
                  Bad Feedback
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className=" font-normal w-1/12">
                  Released
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {displayedReleases?.map((release, index) => {
                const stats = versionStatsMap[release.id] || {
                  instructionsCount: 0,
                  testedInteractionsCount: 0,
                  goodFeedbackCount: 0,
                  badFeedbackCount: 0,
                  topicsCount: 0,
                  topicsList: [],
                };
                if (currentRelease?.id === release.id) {
                  return (
                    <VersionItem
                      key={release.id}
                      index={index}
                      count={releasesWithVersions?.length || 0}
                      version={currentRelease as any}
                      instructionsCount={
                        currentRelease?.topicReleasePolicies?.reduce(
                          (acc: number, policy: any) =>
                            acc + (policy.instructions?.length || 0),
                          0
                        ) || 0
                      }
                      topicsCount={stats.topicsCount}
                      topicsList={stats.topicsList}
                      selectedTopicIds={selectedTopicIds}
                    />
                  );
                }
                // simulator todo: add version stats
                return (
                  <VersionItem
                    key={release.id}
                    index={index}
                    count={releasesWithVersions?.length || 0}
                    version={release as any}
                    instructionsCount={stats.instructionsCount}
                    topicsCount={stats.topicsCount}
                    topicsList={stats.topicsList}
                    selectedTopicIds={selectedTopicIds}
                  />
                );
              })}
            </Table.Body>
          </Table.Root>

          {/* Show More Button */}
          {hasMoreItems && (
            <Flex justify="center" align="center" py="4">
              <Button variant="outline" size="2" onClick={handleShowMore}>
                <Flex gap="2" align="center">
                  Show More
                  <ChevronDown size={14} />
                </Flex>
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
}
